import { Injectable, inject } from "@angular/core";
import { BackendApiService } from "./backend-api.service";
import { BehaviorSubject, Observable, from, of } from "rxjs";
import { map, tap, catchError } from "rxjs/operators";
import { BaseUrlConstantsService } from "./base-url-constants.service";

@Injectable({
  providedIn: "root",
})
export class PiezeoDashboardService {

  url_prefix=`${BaseUrlConstantsService.BASE_PIEZO_SERVICE}`
  // url_prefix='http://localhost:3017'

  private _subsystem_data$: any = new BehaviorSubject<any>(null);
  subsystem_data$: Observable<any> = this._subsystem_data$.asObservable();

  // store gateways here
  private gatewaysSubject = new BehaviorSubject<any[]>([]);
  gateways$: Observable<any[]> = this.gatewaysSubject.asObservable();

  srv = inject(BackendApiService);

  setSubsystemData(data: any) {
    console.log("UPDAING SUBSYSTEM WITH NEW DATA");
    this._subsystem_data$.next(data);
  }

  getSubsystemData() {
    return this._subsystem_data$.value;
  }

  resetSubsystemData() {
    // this._subsystem_data$ = {};
    this.setSubsystemData(null)
    this.gatewaysSubject.next([]);
  }

  /**
   * Fetch gateways for current subsystem and return an Observable.
   * Uses your srv.getDataV2 (Promise-based) under the hood.
   */
  fetchSubsystemGateways(): Observable<any[]> {
    const subsystemId = this._subsystem_data$?.value?.id;

    if (!subsystemId) {
      console.error("Subsystem ID missing in subsystem_data");
      this.gatewaysSubject.next([]);
      return of([]);
    }

    const url = `${this.url_prefix}/api/get-system-gateways-dashboard/${subsystemId}`;

    // Wrap your Promise into an Observable
    return from(this.srv.getDataV2(url)).pipe(
      map((res: any) => {
        if (res?.code === 200 && Array.isArray(res.data)) {
          return res.data; // gateways array
        }
        return [];
      }),
      tap((gateways: any[]) => {
        this.gatewaysSubject.next(gateways); // save in service state
      }),
      catchError((err) => {
        console.error("Error fetching subsystem gateways", err);
        this.gatewaysSubject.next([]);
        return of([]);
      })
    );
  }

  /**
   * Fetch sensor table data for given client + asset IDs.
   * Wraps your srv.getDataV2() Promise into an Observable.
   *
   * Expected API response:
   * {
   *   code: 200,
   *   status: "success",
   *   message: "Sensors Data Fetched Successfully!",
   *   data: {
   *      "676": [ ...metrics ],
   *      "677": [ ...metrics ]
   *   }
   * }
   */
  fetchSensorsTableData(clientId: any, assetIds: string): Observable<any> {
    if (!clientId || !assetIds) {
      console.error("Missing clientId or assetIds for fetchSensorsTableData()");
      return of({});
    }

    // API URL example:
    // GET  /api/get-sensors-table-data/:clientId/:assetIds
    const url = `${this.url_prefix}/api/get-sensors-table-data/${clientId}/${assetIds}`;

    // Convert Promise → Observable
    return from(this.srv.getDataV2(url)).pipe(
      map((res: any) => {
        // Check for valid response
        if (res?.code === 200 && res?.data) {
          return res.data; // return the object: {676:[..], 677:[..]}
        }
        return {}; // fallback empty object
      }),

      // Optional: update a BehaviorSubject if you want to store it
      tap((sensorData) => {
        // Example: this.sensorsSubject.next(sensorData);
      }),

      // Handle errors gracefully
      catchError((err) => {
        console.error("Error fetching sensor table data", err);
        // this.sensorsSubject.next({});
        return of({});
      })
    );
  }
  /**
   * Fetch sensor table data for given client + asset IDs.
   * Wraps your srv.getDataV2() Promise into an Observable.
   *
   * Expected API response:
   * {
   *   code: 200,
   *   status: "success",
   *   message: "Sensors Data Fetched Successfully!",
   *   data: {
   *      "676": [ ...metrics ],
   *      "677": [ ...metrics ]
   *   }
   * }
   */
  fetchEmSensorsTableData(
    clientId: any,
    assetIds: string,
    sid: string
  ): Observable<any> {
    if (!clientId || !assetIds) {
      console.error("Missing clientId or assetIds for fetchSensorsTableData()");
      return of({});
    }

    // API URL example:
    // GET  /api/get-sensors-table-data/:clientId/:assetIds
    const url = `${this.url_prefix}/api/get-sensors-table-data-em/${clientId}/${assetIds}/${sid}`;

    // Convert Promise → Observable
    return from(this.srv.getDataV2(url)).pipe(
      map((res: any) => {
        // Check for valid response
        if (res?.code === 200 && res?.data) {
          return res.data; // return the object: {676:[..], 677:[..]}
        }
        return {}; // fallback empty object
      }),

      // Optional: update a BehaviorSubject if you want to store it
      tap((sensorData) => {
        // Example: this.sensorsSubject.next(sensorData);
      }),

      // Handle errors gracefully
      catchError((err) => {
        console.error("Error fetching sensor table data", err);
        // this.sensorsSubject.next({});
        return of({});
      })
    );
  }

  /**
   * Fetch health bar status for given client, asset IDs, and lastHours.
   *
   * Expected API:
   * GET /api/get-health-bar/:clientId/:assetIds/:lastHours
   *
   * Example:
   *   fetchHealthStatus(2, "676,677", 30)
   *
   * API Response Expected:
   * {
   *   code: 200,
   *   status: "success",
   *   message: "Health Status Fetched Successfully!",
   *   data: { ... }
   * }
   */
  fetchHealthBar(
    clientId: any,
    assetIds: string,
    lastHours: number
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      console.log(clientId, assetIds, lastHours);
      if (!clientId || !assetIds || !lastHours) {
        console.error("Missing parameters in fetchHealthBar()");
        resolve({});
        return;
      }

      const url = `${this.url_prefix}/api/get-health-bar/${clientId}/${assetIds}/${lastHours}`;

      console.log("Calling API URL:", url);

      this.srv
        .getDataV2(url) // <-- MUST return a real Promise
        .then((res: any) => {
          console.log("API RESPONSE:", res);
          if (res?.code === 200 && res?.data) {
            resolve(res.data);
          } else {
            resolve({});
          }
        })
        .catch((err: any) => {
          console.error("Health Bar Error:", err);
          resolve({}); // do not reject, avoid UI crash
        });
    });
  }

  updateSensorInSubsystem(sensorData: any, sid): Observable<any> {
    const url = `${this.url_prefix}/api/update-piezo-axis/${sid}`;
    return from(this.srv.putData(url, sensorData)).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((err) => {
        console.error("Error updating sensor in subsystem", err);
        return of(null);
      })
    );
  }

  fetchGatewayConfig(gid) {
    return new Promise((resolve, reject) => {
      const url = `${this.url_prefix}/api/get-gateway-config/${gid}`;
      this.srv
        .getDataV2(url)
        .then((res: any) => {
          if (res?.code === 200 && res?.data) {
            resolve(res.data);
          } else {
            resolve(null);
          }
        })
        .catch((err: any) => {
          console.error("Error fetching gateway config", err);
          resolve(null);
        });
    });
  }

  updateGatewayConfig(data: any, gid: string): Observable<any> {
    const url = `${this.url_prefix}/api/update-gateway-config/${gid}`;
    return from(this.srv.putData(url, data)).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((err) => {
        console.error("Error updating gateway config", err);
        return of(null);
      })
    );
  }

  deleteGateway(gid: string, sid: string): Observable<any> {
    const url = `${this.url_prefix}/api/delete-gateway/${gid}/${sid}`;
    return from(this.srv.deleteInfo(url)).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((err) => {
        console.error("Error deleting gateway", err);
        return of(null);
      })
    );
  }

  fetchSubsystemData(sid: string): Observable<any> {
    const url = `${this.url_prefix}/api/get-subsystem/${sid}`;

    return from(this.srv.getDataV2(url)).pipe(
      map((res: any) => (res?.code === 200 && res?.data ? res.data : null)),
      tap((data) => {
        if (data) this.setSubsystemData(data); // 🔥 Auto update observable subscribers
      }),
      catchError((err) => {
        console.error("Error fetching subsystem data", err);
        this.setSubsystemData(null); // optional: reset on failure
        return of(null);
      })
    );
  }

  deleteSingleSensor(aid: string, gid: string): Observable<any> {
    const url = `${this.url_prefix}/api/delete-single-sensor/${aid}/${gid}`;
    return from(this.srv.deleteInfo(url)).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((err) => {
        console.error("Error deleting gateway", err);
        return of(null);
      })
    );
  }

  deleteSingleEmSensor(aid: string, sid: string): Observable<any> {
    const url = `${this.url_prefix}/api/delete-single-sensor-em/${aid}/${sid}`;
    return from(this.srv.deleteInfo(url)).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((err) => {
        console.error("Error deleting gateway", err);
        return of(null);
      })
    );
  }

  /**
   * Fetch Piezo Alerts for given client, assetIds and lastHours.
   *
   * Expected API Response:
   * {
   *   code: 200,
   *   status: "success",
   *   message: "Piezo Alerts Fetched Successfully!",
   *   data: { ... }  // or null
   * }
   *
   * Rule:
   *   If API returns valid data → return [data]
   *   Otherwise → return []
   */
  fetchPiezoAlerts(
    clientId: string,
    assetIds: string,
  ): Promise<any[]> {
    return new Promise((resolve, reject) => {
      if (!clientId || !assetIds) {
        console.error("Missing parameters in fetchPiezoAlerts()");
        resolve(null);
        return;
      }

      const url = `${this.url_prefix}/api/get-piezo-alerts/${clientId}/${assetIds}`;

      console.log("Calling Piezo Alerts API:", url);

      this.srv
        .getDataV2(url)
        .then((res: any) => {
          console.log("PIEZO ALERTS RESPONSE:", res);

          // ✔ Only return array if response is correct
          if (res?.code === 200 && res?.data) {
            resolve(res.data); // <-- wrap in array
          } else {
            resolve(null); // no alerts
          }
        })
        .catch((err: any) => {
          console.error("Piezo Alerts API Error:", err);
          resolve(null); // safe fallback
        });
    });
  }

  /**
   * Fetch Emsaver Alerts for given client, assetIds and lastHours.
   *
   * Expected API Response:
   * {
   *   code: 200,
   *   status: "success",
   *   message: "Piezo Alerts Fetched Successfully!",
   *   data: { ... }  // or null
   * }
   *
   * Rule:
   *   If API returns valid data → return [data]
   *   Otherwise → return []
   */
  fetchEmsaverAlerts(
    clientId: string,
    assetIds: string,
  ): Promise<any[]> {
    return new Promise((resolve, reject) => {
      if (!clientId || !assetIds) {
        console.error("Missing parameters in fetchPiezoAlerts()");
        resolve(null);
        return;
      }

      const url = `${this.url_prefix}/api/get-emsaver-alerts/${clientId}/${assetIds}`;

      console.log("Calling Piezo Alerts API:", url);

      this.srv
        .getDataV2(url)
        .then((res: any) => {
          console.log("PIEZO ALERTS RESPONSE:", res);

          // ✔ Only return array if response is correct
          if (res?.code === 200 && res?.data) {
            resolve(res.data); // <-- wrap in array
          } else {
            resolve(null); // no alerts
          }
        })
        .catch((err: any) => {
          console.error("Piezo Alerts API Error:", err);
          resolve(null); // safe fallback
        });
    });
  }
}
